import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  AppAutocomplete,
  AppButton,
  AppCard,
  AppCheckbox,
  AppEmptyState,
  AppFormField,
  AppInput,
  AppSpinner,
} from '../../../components/ui';
import secteursAdminService from '../../../services/secteursAdminService';
import { notify } from '../../../components/common/AppToast';
import { ROUTES } from '../../../config/routes';

const EMPTY = {
  user_id: null,
  userQuery: '',
  email: '',
  nom: '',
  telephone: '',
  couleur: '#6366f1',
  active: true,
  has_photo: false,
  has_custom_photo: false,
};

const getCommercialRoutes = () => ({
  list: ROUTES.admin.commerciaux,
  backLabel: 'Liste des commerciaux',
});

const formatUserLabel = (user) => {
  if (!user) return '';
  const name = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name ? `${name} (${user.email})` : (user.email || '');
};

const SecteursAdminCommercialFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const routes = getCommercialRoutes();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    secteursAdminService.getCommercial(id)
      .then((c) => setForm({
        user_id: c.user_id ?? c.user?.id ?? null,
        userQuery: formatUserLabel(c.user),
        email: c.email || '',
        nom: c.nom || '',
        telephone: c.telephone || '',
        couleur: c.couleur || '#6366f1',
        active: c.active !== false,
        has_photo: Boolean(c.has_photo),
        has_custom_photo: Boolean(c.has_custom_photo),
      }))
      .catch((err) => {
        setLoadError(err.message);
        notify.error(err.message);
      })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    const loadPreview = async () => {
      if (pendingPhotoFile) {
        objectUrl = URL.createObjectURL(pendingPhotoFile);
        if (!cancelled) setPhotoPreviewUrl(objectUrl);
        return;
      }

      if (!isEdit || !form.has_photo) {
        if (!cancelled) setPhotoPreviewUrl(null);
        return;
      }

      const blob = await secteursAdminService.fetchCommercialPhotoBlob(id);
      if (cancelled) return;
      if (!blob) {
        setPhotoPreviewUrl(null);
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      setPhotoPreviewUrl(objectUrl);
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, isEdit, form.has_photo, pendingPhotoFile]);

  useEffect(() => {
    const query = form.userQuery.trim();
    if (query.length < 2) {
      setUserSuggestions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const users = await secteursAdminService.listUsersDisponibles({
          search: query,
          excludeCommercialId: isEdit ? id : null,
          limit: 20,
        });
        if (!cancelled) setUserSuggestions(users);
      } catch {
        if (!cancelled) setUserSuggestions([]);
      } finally {
        if (!cancelled) setUserSearchLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.userQuery, id, isEdit]);

  const handleSelectUser = (user) => {
    setForm((f) => ({
      ...f,
      user_id: user.id,
      userQuery: formatUserLabel(user),
      email: user.email || f.email,
      nom: user.fullName || f.nom,
    }));
    setUserSuggestions([]);
  };

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      notify.error('Choisissez une image (PNG, WebP, JPG)');
      return;
    }
    setPendingPhotoFile(file);
  };

  const handleRemovePhoto = async () => {
    if (pendingPhotoFile) {
      setPendingPhotoFile(null);
      return;
    }
    if (!isEdit || !form.has_custom_photo) {
      notify.error('Seules les photos uploadées via l’admin peuvent être supprimées');
      return;
    }
    setPhotoBusy(true);
    try {
      const updated = await secteursAdminService.deleteCommercialPhoto(id);
      setForm((f) => ({
        ...f,
        has_photo: Boolean(updated?.has_photo),
        has_custom_photo: Boolean(updated?.has_custom_photo),
      }));
      notify.success('Photo personnalisée supprimée');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        user_id: form.user_id || null,
        email: form.email,
        nom: form.nom,
        telephone: form.telephone,
        couleur: form.couleur,
        active: form.active,
      };
      let commercialId = id;
      if (isEdit) {
        await secteursAdminService.updateCommercial(id, payload);
      } else {
        const created = await secteursAdminService.createCommercial(payload);
        commercialId = created?.id;
      }

      if (pendingPhotoFile && commercialId) {
        const updated = await secteursAdminService.uploadCommercialPhoto(commercialId, pendingPhotoFile);
        setPendingPhotoFile(null);
        if (isEdit) {
          setForm((f) => ({
            ...f,
            has_photo: Boolean(updated?.has_photo ?? true),
            has_custom_photo: Boolean(updated?.has_custom_photo ?? true),
          }));
        }
      }

      notify.success(isEdit ? 'Commercial enregistré' : 'Commercial créé');
      navigate(routes.list);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <AppSpinner />
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title={isEdit ? 'Modifier le commercial' : 'Nouveau commercial'}
        actions={(
          <AppButton as={Link} to={routes.list} size="sm" variant="outline-secondary">{routes.backLabel}</AppButton>
        )}
      />

      {loadError ? (
        <AppEmptyState message="Commercial introuvable" />
      ) : (
        <AppCard>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <AppFormField
                className="col-12"
                label="Compte Konvergence"
                htmlFor="commercial-user"
                hint="Optionnel — peut être rattaché plus tard (utile pour Klub / dépôts RDV)"
              >
                <AppAutocomplete
                  id="commercial-user"
                  value={form.userQuery}
                  onChange={(value) => setForm((f) => ({
                    ...f,
                    userQuery: value,
                    user_id: null,
                  }))}
                  onSelect={handleSelectUser}
                  items={userSuggestions}
                  loading={userSearchLoading}
                  getItemKey={(user) => user.id}
                  renderItem={(user) => (
                    <>
                      {user.fullName}
                      <span className="text-secondary-light ms-2 text-sm">{user.email}</span>
                    </>
                  )}
                  placeholder="Rechercher un utilisateur (nom ou email)…"
                  emptyMessage="Aucun compte disponible"
                  loadingLabel="Recherche…"
                  minQueryLength={2}
                />
              </AppFormField>
              <AppFormField className="col-md-6" label="Email" htmlFor="commercial-email" required>
                <AppInput
                  id="commercial-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-6" label="Nom" htmlFor="commercial-nom" required>
                <AppInput
                  id="commercial-nom"
                  type="text"
                  required
                  value={form.nom}
                  onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                />
              </AppFormField>
              <AppFormField
                className="col-md-6"
                label="Téléphone"
                htmlFor="commercial-telephone"
                hint="Utilisé dans les présentations (bilan audience, plan de com…)"
              >
                <AppInput
                  id="commercial-telephone"
                  type="tel"
                  placeholder="06 12 34 56 78"
                  value={form.telephone}
                  onChange={(e) => setForm((f) => ({ ...f, telephone: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-3" label="Couleur" htmlFor="commercial-couleur">
                <AppInput
                  id="commercial-couleur"
                  type="color"
                  className="form-control-color"
                  value={form.couleur}
                  onChange={(e) => setForm((f) => ({ ...f, couleur: e.target.value }))}
                />
              </AppFormField>
              <div className="col-md-3 d-flex align-items-end">
                <AppCheckbox
                  id="commercial-active"
                  switch
                  label="Actif"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  wrapperClassName="mb-2"
                />
              </div>

              <AppFormField
                className="col-12"
                label="Photo (présentations)"
                htmlFor="commercial-photo"
                hint="Utilisée dans les bilans audience et plans de com (PNG, WebP, JPG — max 5 Mo)"
              >
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div
                    className="border rounded bg-light d-flex align-items-center justify-content-center overflow-hidden"
                    style={{ width: 96, height: 96 }}
                  >
                    {photoPreviewUrl ? (
                      <img
                        src={photoPreviewUrl}
                        alt={`Photo de ${form.nom || 'commercial'}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span className="text-muted text-sm px-2 text-center">Aucune photo</span>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <input
                      ref={fileInputRef}
                      id="commercial-photo"
                      type="file"
                      accept="image/png,image/webp,image/jpeg"
                      className="d-none"
                      onChange={handlePhotoPick}
                    />
                    <AppButton
                      type="button"
                      size="sm"
                      variant="outline-primary"
                      icon="mdi:image-plus"
                      loading={photoBusy}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {photoPreviewUrl ? 'Changer la photo' : 'Ajouter une photo'}
                    </AppButton>
                    {(pendingPhotoFile || form.has_custom_photo) && (
                      <AppButton
                        type="button"
                        size="sm"
                        variant="outline-danger"
                        icon="mdi:delete"
                        loading={photoBusy}
                        onClick={handleRemovePhoto}
                      >
                        Supprimer
                      </AppButton>
                    )}
                    {pendingPhotoFile && (
                      <span className="text-muted text-sm">
                        Nouvelle photo sélectionnée — sera enregistrée à la sauvegarde
                      </span>
                    )}
                  </div>
                </div>
              </AppFormField>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <AppButton as={Link} to={routes.list} variant="secondary">Annuler</AppButton>
              <AppButton type="submit" variant="success" loading={saving} loadingLabel="Enregistrement…">
                Enregistrer
              </AppButton>
            </div>
          </form>
        </AppCard>
      )}
    </MasterLayout>
  );
};

export default SecteursAdminCommercialFormPage;
